// src/feature-module/crm/deals/components/SearchComponent.tsx
import React, { useState, useEffect, useRef } from 'react';

interface SearchComponentProps {
  onSearch: (searchTerm: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  initialValue?: string;
  debounceDelay?: number;
}

const SearchComponent: React.FC<SearchComponentProps> = ({
  onSearch,
  placeholder = "Pesquisar por título, contato ou número do contrato...",
  isLoading = false,
  initialValue = "",
  debounceDelay = 500
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Função para limpar timeout anterior
  const clearDebounceTimeout = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  };

  // Effect para debounce da pesquisa
  useEffect(() => {
    clearDebounceTimeout();

    if (searchTerm.length >= 2 || searchTerm.length === 0) {
      setIsSearching(true);
      
      debounceTimeoutRef.current = setTimeout(() => {
        onSearch(searchTerm);
        setIsSearching(false);
      }, debounceDelay);
    }

    return () => clearDebounceTimeout();
  }, [searchTerm, onSearch, debounceDelay]);

  // Função para lidar com mudanças no input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Função para limpar pesquisa
  const handleClearSearch = () => {
    setSearchTerm("");
    inputRef.current?.focus();
  };

  // Função para pesquisa imediata (quando pressiona Enter)
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      clearDebounceTimeout();
      setIsSearching(true);
      onSearch(searchTerm);
      setIsSearching(false);
    }
  };

  // Cleanup no unmount
  useEffect(() => {
    return () => clearDebounceTimeout();
  }, []);

  return (
    <div className="search-component">
      <div className="search-input position-relative">
        <div className="icon-form mb-3 mb-sm-0">
          <span className="form-icon">
            {(isSearching || isLoading) ? (
              <i className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <i className="ti ti-search" />
            )}
          </span>
          <input
            ref={inputRef}
            type="text"
            className="form-control"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          {searchTerm && (
            <button
              className="btn btn-sm position-absolute"
              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
              onClick={handleClearSearch}
              type="button"
              title="Limpar pesquisa"
            >
              <i className="ti ti-x" />
            </button>
          )}
        </div>
      </div>

      {/* Informações sobre a pesquisa */}
      {searchTerm && (
        <div className="mt-2">
          <small className="text-muted">
            <i className="ti ti-info-circle me-1" />
            Pesquisando por: <strong>"{searchTerm}"</strong>
            {searchTerm.length < 2 && (
              <span className="text-warning"> (mínimo 2 caracteres)</span>
            )}
          </small>
        </div>
      )}

      
    </div>
  );
};

export default SearchComponent;